import ms from 'ms';
import {
  actionChannel,
  all,
  call,
  put,
  race,
  select,
  take,
  takeLeading,
} from 'typed-redux-saga';
import { OktaService } from '../../services/okta';
import { SettingsService } from '../../services/settings';
import { StreamService } from '../../services/stream';
import { watchGetPrayerStreamUserRegistrationSaga } from '../async-action/get-prayer-stream-user-registration/get-prayer-stream-user-registration.saga';
import { type RootState } from '../root.state';
import { prayerStreamSaga } from '../stream/stream.saga';
import { streamSlice } from '../stream/stream.slice';
import { getContainer } from '../utils.saga';
import { authSlice } from './auth.slice';

export const SLEEP_DETECT_INTERVAL = ms('30s');

export function* loginSaga(): Generator {
  const container = yield* getContainer();
  const { oktaAuth } = container.get(OktaService);
  const { settings } = container.get(SettingsService);

  yield oktaAuth.signInWithRedirect({
    extraParams: {
      language: 'en',
    },
    maxAge: settings.loginMaxAgeSeconds,
  });
}

export function* logoutSaga(payload: {
  continueRoutePostLogout: string | null;
}): Generator {
  const container = yield* getContainer();
  const { oktaAuth } = container.get(OktaService);
  const { client } = container.get(StreamService);

  yield* put(authSlice.actions.logoutStarted());

  if (payload.continueRoutePostLogout) {
    yield* put(
      authSlice.actions.setContinueRoutePostLogout(
        payload.continueRoutePostLogout,
      ),
    );
  }
  yield* put(streamSlice.actions.clearStreamData());
  yield* call(async () => {
    await client.disconnectUser();
  });

  yield* call(async () => oktaAuth.signOut());
}

export function* watchLoginRequestsSaga(): Generator {
  yield* takeLeading(authSlice.actions.loginRequested, loginSaga);
}

export function* watchLogoutRequestsSaga(): Generator {
  const action = yield* take(authSlice.actions.logoutRequested);

  yield* call(logoutSaga, {
    continueRoutePostLogout: action.payload.continueRoutePostLogout ?? null,
  });
}

export function* preLogoutPendingSaga(): Generator {
  yield* race({
    watchAuthenticatedRequests: all([
      watchGetPrayerStreamUserRegistrationSaga(),
      prayerStreamSaga(),
    ]),
    watchLogoutPending: take([
      authSlice.actions.logoutStarted,
      authSlice.actions.logoutSucceeded,
      authSlice.actions.setLoggedOut,
    ]),
  });
}

export function* authSaga(): Generator {
  const logoutSucceededChannel = yield* actionChannel(
    authSlice.actions.logoutSucceeded,
  );
  const loginSucceededChannel = yield* actionChannel(
    authSlice.actions.loginSucceeded,
  );

  while (true) {
    const { loggedIn } = yield* select((state: RootState) => state.auth);

    yield* loggedIn
      ? race({
          watchAuthenticatedRequests: all([
            watchLogoutRequestsSaga(),
            preLogoutPendingSaga(),
          ]),
          watchLogoutRequests: take(logoutSucceededChannel),
        })
      : race({
          watchLoginRequests: take(loginSucceededChannel),
          watchUnauthenticatedRequests: all([watchLoginRequestsSaga()]),
        });
  }
}
